"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  Mail,
  Phone,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ── WhatsApp badge (brand mark, carries its own colored background) ─────────

function WhatsAppBadge({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" className={className} aria-hidden="true">
      <defs>
        <linearGradient
          id="wa-gradient"
          x1="1337.28"
          y1="518.24"
          x2="1337.28"
          y2="-2164.82"
          gradientTransform="matrix(0.19, 0, 0, -0.19, 0.81, 98.89)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#61fd7d" />
          <stop offset="1" stopColor="#2bb826" />
        </linearGradient>
      </defs>
      <path
        fill="url(#wa-gradient)"
        fillRule="evenodd"
        d="M512,382.07c0,2.8-.09,8.88-.26,13.58-.41,11.49-1.32,26.32-2.7,33.07a109.76,109.76,0,0,1-9.27,27.71,98.45,98.45,0,0,1-43.43,43.39,110.21,110.21,0,0,1-27.87,9.28c-6.69,1.35-21.41,2.24-32.82,2.65-4.71.17-10.79.25-13.58.25l-252.1,0c-2.8,0-8.88-.09-13.58-.26-11.49-.41-26.32-1.32-33.07-2.69a110.37,110.37,0,0,1-27.72-9.28A98.5,98.5,0,0,1,12.18,456.3,110.21,110.21,0,0,1,2.9,428.43C1.55,421.74.66,407,.25,395.61.08,390.91,0,384.82,0,382l0-252.1c0-2.8.09-8.88.25-13.58C.71,104.86,1.62,90,3,83.28a110.37,110.37,0,0,1,9.27-27.72A98.59,98.59,0,0,1,55.7,12.18,110.21,110.21,0,0,1,83.57,2.9C90.26,1.55,105,.66,116.39.25,121.09.08,127.18,0,130,0l252.1,0c2.8,0,8.88.09,13.58.25C407.14.71,422,1.62,428.72,3a110.37,110.37,0,0,1,27.72,9.27A98.59,98.59,0,0,1,499.82,55.7a110.21,110.21,0,0,1,9.28,27.87c1.35,6.69,2.24,21.41,2.65,32.82.17,4.7.25,10.79.25,13.58Z"
      />
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M379.56,131.67A172.4,172.4,0,0,0,256.67,80.73C161,80.73,83.05,158.64,83.05,254.42a173.47,173.47,0,0,0,23.2,86.82l-24.65,90,92.08-24.17a173.55,173.55,0,0,0,83,21.17h.07c95.73,0,173.69-77.91,173.69-173.69A172.73,172.73,0,0,0,379.53,131.7l0,0ZM256.72,399a144.17,144.17,0,0,1-73.52-20.14l-5.29-3.15L123.27,390l14.59-53.27-3.42-5.47a143.29,143.29,0,0,1-22.11-76.81C112.33,174.81,177.1,110,256.8,110A144.34,144.34,0,0,1,401.12,254.48c-.07,79.67-64.83,144.46-144.41,144.46v0ZM335.87,290.8c-4.32-2.2-25.68-12.67-29.65-14.12s-6.85-2.19-9.8,2.2-11.22,14.11-13.76,17-5.06,3.29-9.37,1.09-18.35-6.77-34.92-21.56c-12.88-11.5-21.61-25.74-24.15-30s-.29-6.71,1.92-8.83c2-1.93,4.32-5.06,6.51-7.6s2.88-4.32,4.32-7.26.74-5.42-.35-7.6-9.8-23.55-13.34-32.25c-3.49-8.51-7.12-7.32-9.79-7.47s-5.42-.13-8.29-.13a16,16,0,0,0-11.57,5.41c-4,4.32-15.2,14.86-15.2,36.22s15.54,42,17.72,44.91,30.61,46.76,74.14,65.54c10.34,4.44,18.42,7.11,24.72,9.18a60,60,0,0,0,27.32,1.71c8.35-1.23,25.68-10.49,29.31-20.62s3.63-18.83,2.55-20.62-3.91-3-8.29-5.22l0,0Z"
      />
    </svg>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────

type ContactChannel = {
  id: string;
  icon?: typeof Mail;
  brandIcon?: boolean;
  title: string;
  description: string;
  actionLabel: string;
  href: string;
  external?: boolean;
};

const CHANNELS: ContactChannel[] = [
  {
    id: "chat",
    brandIcon: true,
    title: "WhatsApp us",
    description: "Fastest for quick questions, Monday to Saturday, 9am to 6pm GST",
    actionLabel: "Start a WhatsApp chat",
    href: "https://wa.me/971589361909?text=Hello%2C%20I%20need%20assistance%20with%20gold%20investment",
    external: true,
  },
  {
    id: "email",
    icon: Mail,
    title: "Email us",
    description: "We'll get back to you within 24 hours",
    actionLabel: "support@justgold.app",
    href: "mailto:support@justgold.app",
    external: true,
  },
  {
    id: "call",
    icon: Phone,
    title: "Call us",
    description: "Need help? Get in touch, Monday to Saturday, 9am to 6pm GST",
    actionLabel: "+971 58 936 1909",
    href: "tel:+971589361909",
    external: true,
  },
];

const REASONS = [
  "Account or verification help",
  "Buy, sell, or delivery question",
  "Partnership or media enquiry",
  "Report a security concern",
];

// ── Motion ────────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

const headlineStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.06 } },
};

function revealLine(reduce: boolean): Variants {
  return {
    hidden: {
      opacity: 0,
      y: reduce ? 0 : "112%",
      filter: reduce ? "none" : "blur(8px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.78, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Field styles ──────────────────────────────────────────────────────────────

const fieldClassName =
  "mt-2 min-h-12 w-full rounded-card-sm border border-[#e3ecfb] bg-white px-4 text-base text-primary-900 outline-none transition-colors duration-200 placeholder:text-primary-300 hover:border-primary-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-600/15";

// ── Section ───────────────────────────────────────────────────────────────────

export function ContactPage() {
  const reduce = useReducedMotion() ?? false;
  const [reason, setReason] = useState<string>(REASONS[0]);
  const [sent, setSent] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();
    const subject = encodeURIComponent(`JustGold support: ${reason}`);
    const body = encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\nReason: ${reason}\n\n${message}`
    );
    window.location.href = `mailto:support@justgold.app?subject=${subject}&body=${body}`;
    setSent(true);
  }

  return (
    <div className="bg-neutral-0">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden bg-primary-900"
        aria-labelledby="contact-heading"
      >
        <div className="relative mx-auto max-w-[1200px] px-6 pt-12 pb-10 sm:px-10 sm:pt-14 sm:pb-12 lg:pt-16 lg:pb-14">
          <motion.div
            variants={container}
            initial="hidden"
            animate="visible"
            className="mx-auto flex max-w-[720px] flex-col items-center text-center"
          >
            <motion.p
              variants={fadeUp(reduce)}
              className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-300"
            >
              <span className="h-px w-4 bg-accent-300" aria-hidden="true" />
              Get in touch
            </motion.p>
            <motion.h1
              id="contact-heading"
              variants={headlineStagger}
              className="text-[clamp(1.85rem,3.6vw,2.75rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-white"
            >
              <motion.span variants={revealLine(reduce)} className="block">
                We&apos;re here to help you own gold.
              </motion.span>
            </motion.h1>
            <motion.p
              variants={fadeUp(reduce)}
              className="mt-4 max-w-[540px] text-base leading-relaxed text-primary-200"
            >
              Questions about your account, a purchase, or getting started? Our
              team in Dubai is happy to help. Pick whichever way suits you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Channels ─────────────────────────────────────────────────────── */}
      <motion.section
        aria-label="Contact channels"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className="bg-neutral-0"
      >
        <div className="mx-auto max-w-[1000px] px-6 sm:px-10">
          <div className="grid gap-4 pt-8 pb-4 sm:grid-cols-3 sm:gap-5 sm:pt-10">
            {CHANNELS.map(
              ({ id, icon: Icon, brandIcon, title, description, actionLabel, href, external }) => (
                <motion.a
                  key={id}
                  variants={fadeUp(reduce)}
                  href={href}
                  {...(external
                    ? { target: id === "chat" ? "_blank" : undefined, rel: id === "chat" ? "noopener noreferrer" : undefined }
                    : {})}
                  className="group flex flex-col gap-3 rounded-card border border-[#e3ecfb] bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_18px_40px_-24px_rgba(10,45,110,0.28)]"
                >
                  <div className="flex items-center justify-between">
                    {brandIcon ? (
                      <WhatsAppBadge className="h-10 w-10 shrink-0" />
                    ) : (
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600 transition-colors duration-300 group-hover:bg-primary-100"
                        aria-hidden="true"
                      >
                        {Icon ? (
                          <Icon className="h-5 w-5" strokeWidth={1.8} />
                        ) : null}
                      </span>
                    )}
                    <ChevronRight
                      className="h-5 w-5 shrink-0 text-primary-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-primary-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold leading-tight tracking-[-0.01em] text-primary-900">
                      {title}
                    </h2>
                    <p className="mt-1 text-sm leading-snug text-text-body">
                      {description}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-primary-600">
                      {actionLabel}
                    </p>
                  </div>
                </motion.a>
              )
            )}
          </div>
        </div>
      </motion.section>

      {/* ── Form + Side info ─────────────────────────────────────────────── */}
      <motion.section
        aria-labelledby="contact-form-heading"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="bg-neutral-0"
      >
        <div className="mx-auto grid max-w-[1200px] gap-10 px-6 pb-16 pt-14 sm:px-10 sm:pb-20 sm:pt-16 lg:grid-cols-[1.2fr_0.8fr] lg:items-start lg:gap-14 lg:pb-24 lg:pt-20">
          {/* Left — form */}
          <motion.div variants={fadeUp(reduce)}>
            <h2
              id="contact-form-heading"
              className="text-[26px] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
            >
              Prefer to write?
            </h2>
            <p className="mt-2.5 max-w-[480px] text-sm leading-relaxed text-text-muted">
              Fill in the form and we&apos;ll route your note to the team best
              placed to help. Most enquiries get a reply within one working
              day.
            </p>

            {sent ? (
              <div className="mt-7 rounded-xl border border-success/25 bg-success-bg p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-success">
                  <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden="true" />
                  Thanks. We&apos;ve opened an email draft with your details —
                  send it from your mail app and we&apos;ll be in touch
                  shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="mt-3 text-sm font-semibold text-primary-600 hover:text-primary-800"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-[18px]">
                <label className="block text-[13px] font-semibold text-primary-900">
                  What&apos;s this about?
                  <select
                    name="reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    className={fieldClassName}
                  >
                    {REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-[13px] font-semibold text-primary-900">
                  Your name
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    required
                    placeholder="Enter your name"
                    className={fieldClassName}
                  />
                </label>

                <label className="block text-[13px] font-semibold text-primary-900">
                  Email address
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    className={fieldClassName}
                  />
                </label>

                <label className="block text-[13px] font-semibold text-primary-900">
                  Message
                  <textarea
                    name="message"
                    required
                    rows={5}
                    placeholder="Tell us how we can help"
                    className={cn(fieldClassName, "resize-y py-3")}
                  />
                  <span className="mt-1.5 block text-xs text-text-muted">
                    Please don&apos;t include passwords, codes, or payment
                    details.
                  </span>
                </label>

                <button
                  type="submit"
                  className="mt-2 inline-flex w-fit items-center justify-center rounded-full bg-primary-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                >
                  Send message
                </button>
              </form>
            )}
          </motion.div>

          {/* Right — sidebar info */}
          <motion.aside variants={fadeUp(reduce)} className="flex flex-col gap-[18px]">
            <div className="rounded-card border border-[#e3ecfb] bg-[#f7faff] p-6">
              <h3 className="text-[15px] font-semibold text-primary-900">
                Based in Dubai
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-body">
                Serving savers across the UAE
              </p>
            </div>

            <div className="rounded-card border border-[#e3ecfb] bg-[#f7faff] p-6">
              <h3 className="text-[15px] font-semibold text-primary-900">
                Support hours
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-body">
                Monday to Saturday, 9am to 6pm GST
              </p>
            </div>

            <div className="rounded-card border border-[#e3ecfb] bg-[#f7faff] p-6">
              <h3 className="text-[15px] font-semibold text-primary-900">
                Stay safe
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-body">
                Never share passwords, verification codes, or payment
                details.
              </p>
            </div>

            <div className="rounded-card border border-[#e3ecfb] bg-[#f7faff] p-6">
              <h3 className="text-[15px] font-semibold text-primary-900">
                Looking for a quick answer?
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-body">
                Most common questions are covered in our FAQs.
              </p>
              <Link
                href="/faqs"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-800"
              >
                Browse FAQs
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </motion.aside>
        </div>
      </motion.section>
    </div>
  );
}
